import type { Dictionary } from './de';
import { enCampaigns } from './en.campaigns';
import { enDialer } from './en.dialer';
import { enPlatform } from './en.platform';
import { enUi } from './en.ui';
import { enTelephony } from './en.telephony';

export const en: Dictionary = {
  ...enCampaigns,
  ...enUi,
  ...enDialer,
  ...enPlatform,
  ...enTelephony,
  'app.name': 'Autodialer',
  'app.loading': 'One moment …',

  'action.login': 'Sign in',
  'action.logout': 'Sign out',
  'action.save': 'Save',
  'action.cancel': 'Cancel',
  'action.back': 'Back',
  'action.create': 'Create',
  'action.showPassword': 'Show password',
  'action.hidePassword': 'Hide password',

  'login.title': 'Sign in',
  'login.identifier': 'Email address or user name',
  'login.identifierHint': 'Account owners sign in with their email address, callers with their user name.',
  'login.password': 'Password',
  'login.forgotPassword': 'Forgot your password?',
  'login.noAccount': 'No account yet? Register now',
  'login.platformTitle': 'Administration',
  'login.email': 'Email address',

  'register.title': 'Create account',
  'register.email': 'Email address',
  'register.password': 'Password',
  'register.passwordConfirmation': 'Repeat password',
  'register.submit': 'Create account',
  'register.haveAccount': 'Already have an account? Sign in',
  'register.successTitle': 'Welcome',
  'register.successText': 'Your account is ready. You can sign in now.',
  'register.customerNumber': 'Your customer number',
  'register.customerNumberHint': 'Please keep this number at hand when you contact support.',

  'forgot.title': 'Forgot password',
  'forgot.text': 'Enter your email address. If an account exists, we will send you a link to set a new password.',
  'forgot.submit': 'Request link',
  'forgot.done': 'If an account exists for this address, the email is on its way. Please check your inbox.',
  'forgot.callerHint': 'Caller accounts have no password reset. Please ask your administrator.',

  'reset.title': 'Set a new password',
  'reset.password': 'New password',
  'reset.submit': 'Save password',
  'reset.done': 'Your password has been saved. You can sign in now.',

  'home.greeting': 'Welcome',
  'home.role.owner': 'Account owner',
  'home.role.admin': 'Administrator',
  'home.role.caller': 'Caller',
  'home.customerNumber': 'Customer number',
  'home.nextSteps': 'The areas for campaigns, records and calling will follow in the next steps.',

  'nav.home': 'Home',
  'nav.callers': 'Callers',
  'nav.account': 'Settings',

  'callers.title': 'Callers',
  'callers.intro': 'Create the accounts for your callers here.',
  'callers.username': 'User name',
  'callers.password': 'Password',
  'callers.add': 'Add caller',
  'callers.empty': 'No callers yet.',
  'callers.isAdmin': 'Administrator',
  'callers.makeAdmin': 'Make administrator',
  'callers.revokeAdmin': 'Remove administrator rights',
  'callers.deactivate': 'Deactivate account',
  'callers.activate': 'Activate account',
  'callers.newPassword': 'Set a new password',
  'callers.newPasswordSaved': 'The new password has been saved.',
  'callers.inactive': 'Deactivated',
  'callers.you': 'You',

  'account.title': 'Settings',
  'account.name': 'Company or account name',
  'account.nameSaved': 'Saved.',
  'account.email': 'Email address',
  'account.customerNumber': 'Customer number',
  'account.balance': 'Credit',
  'account.pricePerMinute': 'Price per talk minute',
  'account.changePassword': 'Change password',
  'account.currentPassword': 'Current password',
  'account.newPassword': 'New password',
  'account.passwordSaved': 'Your password has been changed.',
  'account.language': 'Language',
  'account.delete': 'Close account',
  'account.deleteWarning':
    'This closes your account. You and your team will no longer be able to sign in. This cannot be undone.',
  'account.deleteConfirm': 'Yes, close the account permanently',
  'account.deletePassword': 'Please enter your password to confirm.',

  'session.endedTitle': 'You have been signed out',
  'session.ended.replaced': 'You signed in from another window. For security reasons only one session at a time is possible.',
  'session.ended.idle': 'You were inactive for an hour. Please sign in again.',
  'session.ended.suspended': 'Account locked. Please contact support.',
  'session.ended.deleted': 'This account has been closed.',
  'session.ended.password_changed': 'Your password was changed. Please sign in again.',
  'session.ended.logout': 'Please sign in.',

  'error.auth.invalid_credentials': 'Those details do not match. Please try again.',
  'error.auth.too_many_attempts': 'Too many attempts. Please wait two minutes and try again.',
  'error.auth.account_suspended': 'Account locked. Please contact support.',
  'error.auth.user_inactive': 'This account is deactivated. Please ask your administrator.',
  'error.auth.required': 'Please sign in.',
  'error.auth.admin_required': 'You need administrator rights for this area.',
  'error.registration.email_taken': 'An account already exists for this email address.',
  'error.registration.invalid_email': 'Please enter a valid email address.',
  'error.registration.password_mismatch': 'The two passwords do not match.',
  'error.registration.password_required': 'Please enter a password.',
  'error.caller.username_taken': 'This user name is already taken. Please choose another one.',
  'error.caller.invalid_username': 'The user name may only contain letters, digits, dot, hyphen and underscore.',
  'error.caller.password_required': 'Please set a password.',
  'error.caller.not_found': 'This account was not found.',
  'error.caller.owner_keeps_admin': 'Administrator rights cannot be removed from the account owner.',
  'error.caller.owner_stays_active': 'The account owner cannot be deactivated.',
  'error.password.current_wrong': 'The current password is not correct.',
  'error.password.reset_token_invalid': 'This link is no longer valid. Please request a new one.',
  'error.account.only_owner_may_delete': 'Only the account owner can close the account.',
  'error.account.confirmation_required': 'Please confirm explicitly.',
  'error.request.invalid': 'Please check your entries.',
  'error.unknown': 'That did not work. Please try again.',
  'error.offline': 'The application is not reachable right now. Please try again later.',
};
